import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useForm } from "@tanstack/react-form";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { RequiredInput } from "@/components/common/required-input";
import { Separator } from "@/components/ui/separator";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  PAYMENT_METHOD_OPTIONS,
  SALE_LINE_ROW_GRID,
  SALE_PAYMENT_ROW_GRID,
} from "@/constants/sales/sales-constants";
import {
  EMPTY_SALE_PAYMENT_LINE,
  EMPTY_SALE_PRODUCT_LINE,
  EMPTY_SALE_SERVICE_LINE,
  getWalkInSaleFormValues,
} from "@/default-form-values/sales/default-sale-form-values";
import { useGetProductsPaginated } from "@/hooks/products/use-products";
import { useCreateWalkInSale } from "@/hooks/sales/use-sales";
import {
  useGetStaffPaginated,
  useGetStaffServicesSummary,
} from "@/hooks/staff/use-staff";
import {
  APPOINTMENT_TOAST_DURATION_MS,
  appointmentToastMessage,
} from "@/lib/appointments/appointment-toast";
import {
  getServiceOptions,
  staffLabel,
} from "@/lib/appointments/appointment-form-utils";
import { useSalonToday } from "@/hooks/me/use-salon";
import { getErrorMessage, isConflictError } from "@/lib/handle-mutation-error";
import {
  computeSaleFormTotal,
  paymentsMatchTotal,
} from "@/lib/sales/sale-form-utils";
import { toWalkInSaleCreate } from "@/mappers/sales/sale-form-to-request";
import type { StaffServicesSummaryResponse } from "@/types/staff/api-types/staff-services-summary-types";
import { walkInSaleSchema } from "@/zod-schemas/sales/walk-in-sale-schema";
import { useIntl } from "react-intl";

interface WalkInSaleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const catalogDurationAndPrice = (
  staffId: number,
  serviceId: number,
  servicesSummary: StaffServicesSummaryResponse | undefined
): { duration_minutes: number; price: number } | null => {
  const offered = getServiceOptions(staffId, servicesSummary).find(
    (item) => item.service_id === serviceId
  );
  if (offered?.duration_minutes == null || offered.price == null) return null;
  return {
    duration_minutes: offered.duration_minutes,
    price: offered.price,
  };
};

export function WalkInSaleDialog({
  open,
  onOpenChange,
}: WalkInSaleDialogProps) {
  const intl = useIntl();
  const today = useSalonToday();
  const createWalkIn = useCreateWalkInSale();
  const { data: staffResponse } = useGetStaffPaginated({
    active_only: true,
    size: 100,
  });
  const { data: productsResponse } = useGetProductsPaginated({ size: 100 });
  const { data: servicesSummary, isError: servicesSummaryIsError } =
    useGetStaffServicesSummary();
  const staffList = staffResponse?.items ?? [];
  const products = productsResponse?.items ?? [];
  const servicesSummaryReady =
    servicesSummary !== undefined && !servicesSummaryIsError;
  const form = useForm({
    defaultValues: getWalkInSaleFormValues(),
    validators: {
      onSubmit: walkInSaleSchema,
    },
    onSubmit: async ({ value }) => {
      const prepared = {
        ...value,
        services: value.services.map((line) => {
          const catalog = catalogDurationAndPrice(
            line.staff_id,
            line.service_id,
            servicesSummary
          );
          return {
            ...line,
            duration_minutes:
              line.duration_minutes >= 1
                ? line.duration_minutes
                : (catalog?.duration_minutes ?? line.duration_minutes),
          };
        }),
      };
      const validated = walkInSaleSchema.parse(prepared);
      try {
        await createWalkIn.mutateAsync(toWalkInSaleCreate(validated));
        toast.success(
          appointmentToastMessage({
            intl,
            action: "completed",
            variant: "success",
            clientName: "Walk in",
            appointmentDate: today,
          }),
          { duration: APPOINTMENT_TOAST_DURATION_MS }
        );
      } catch (error) {
        if (isConflictError(error)) {
          toast.error(getErrorMessage(error));
          return;
        }
        toast.error(
          appointmentToastMessage({
            intl,
            action: "completed",
            variant: "error",
            clientName: "Walk in",
            appointmentDate: today,
          }),
          { duration: APPOINTMENT_TOAST_DURATION_MS }
        );
        return;
      }
      form.reset(getWalkInSaleFormValues());
      onOpenChange(false);
    },
  });

  const applyCatalog = (index: number, staffId: number, serviceId: number) => {
    if (!staffId || !serviceId) return;
    const catalog = catalogDurationAndPrice(
      staffId,
      serviceId,
      servicesSummary
    );
    if (!catalog) return;
    form.setFieldValue(
      `services[${index}].duration_minutes`,
      catalog.duration_minutes
    );
    form.setFieldValue(`services[${index}].price`, Number(catalog.price));
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        onOpenChange(isOpen);
        if (!isOpen) form.reset(getWalkInSaleFormValues());
      }}
    >
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>Sale for Walk in</DialogTitle>
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>{intl.formatDate(today, { format: "long" })}</span>
          </div>
        </DialogHeader>
        <Separator />

        <form
          className="mb-4"
          id="walk-in-sale-form"
          onSubmit={(e) => {
            e.preventDefault();
            form.handleSubmit();
          }}
        >
          <FieldGroup>
            <form.Field
              name="services"
              mode="array"
              children={(linesField) => (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Services</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Add service"
                      onClick={() =>
                        linesField.pushValue({ ...EMPTY_SALE_SERVICE_LINE })
                      }
                    >
                      <Plus />
                    </Button>
                  </div>
                  {linesField.state.value.map((_, index) => (
                    <form.Subscribe
                      key={index}
                      selector={(state) => state.values.services[index]}
                      children={(line) => {
                        if (!line) return null;
                        const serviceOptions = getServiceOptions(
                          line.staff_id,
                          servicesSummary
                        );
                        const noServicesAssigned =
                          line.staff_id !== 0 &&
                          servicesSummaryReady &&
                          serviceOptions.length === 0;
                        return (
                          <div className={SALE_LINE_ROW_GRID}>
                            <form.Field
                              name={`services[${index}].staff_id`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Stylist
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      value={
                                        field.state.value
                                          ? String(field.state.value)
                                          : undefined
                                      }
                                      onValueChange={(value) => {
                                        const next = Number(value);
                                        if (next === field.state.value) return;
                                        field.handleChange(next);
                                        const offered = getServiceOptions(
                                          next,
                                          servicesSummary
                                        ).find(
                                          (item) =>
                                            item.service_id === line.service_id
                                        );
                                        if (offered) {
                                          applyCatalog(
                                            index,
                                            next,
                                            line.service_id
                                          );
                                          return;
                                        }
                                        if (line.service_id === 0) return;
                                        form.setFieldValue(
                                          `services[${index}].service_id`,
                                          0
                                        );
                                        form.setFieldValue(
                                          `services[${index}].duration_minutes`,
                                          0
                                        );
                                        form.setFieldValue(
                                          `services[${index}].price`,
                                          0
                                        );
                                      }}
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {staffList.map((member) => (
                                          <SelectItem
                                            key={member.id}
                                            value={String(member.id)}
                                          >
                                            {staffLabel(member)}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`services[${index}].service_id`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                const stylistChosen = line.staff_id !== 0;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Service
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      key={`walk-in-service-${index}-${line.staff_id}-${line.service_id}`}
                                      value={
                                        line.service_id
                                          ? String(line.service_id)
                                          : ""
                                      }
                                      onValueChange={(value) => {
                                        const next = Number(value);
                                        field.handleChange(next);
                                        applyCatalog(
                                          index,
                                          line.staff_id,
                                          next
                                        );
                                      }}
                                      disabled={!stylistChosen}
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {serviceOptions.map((service) => (
                                          <SelectItem
                                            key={service.service_id}
                                            value={String(service.service_id)}
                                          >
                                            {service.service_name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {noServicesAssigned && (
                                      <FieldDescription>
                                        No services assigned for this stylist
                                      </FieldDescription>
                                    )}
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`services[${index}].price`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Price
                                      <RequiredInput />
                                    </FieldLabel>
                                    <div className="relative">
                                      <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                                      >
                                        $
                                      </span>
                                      <Input
                                        id={field.name}
                                        name={field.name}
                                        type="number"
                                        step="0.01"
                                        value={field.state.value}
                                        onBlur={field.handleBlur}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        aria-invalid={isInvalid}
                                        className="pl-6"
                                      />
                                    </div>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="mt-7"
                              aria-label="Remove service"
                              onClick={() => linesField.removeValue(index)}
                            >
                              <Trash2 />
                            </Button>
                          </div>
                        );
                      }}
                    />
                  ))}
                </>
              )}
            />

            <Separator />

            <form.Field
              name="products"
              mode="array"
              children={(linesField) => (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Products</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Add product"
                      onClick={() =>
                        linesField.pushValue({ ...EMPTY_SALE_PRODUCT_LINE })
                      }
                    >
                      <Plus />
                    </Button>
                  </div>
                  {linesField.state.value.map((_, index) => (
                    <form.Subscribe
                      key={index}
                      selector={(state) => state.values.products[index]}
                      children={(line) => {
                        if (!line) return null;
                        return (
                          <div className={SALE_LINE_ROW_GRID}>
                            <form.Field
                              name={`products[${index}].product_id`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Product
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      value={
                                        field.state.value
                                          ? String(field.state.value)
                                          : undefined
                                      }
                                      onValueChange={(value) => {
                                        const next = Number(value);
                                        field.handleChange(next);
                                        const product = products.find(
                                          (item) => item.id === next
                                        );
                                        if (product) {
                                          form.setFieldValue(
                                            `products[${index}].price`,
                                            Number(product.retail_price)
                                          );
                                        }
                                      }}
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {products.map((product) => (
                                          <SelectItem
                                            key={product.id}
                                            value={String(product.id)}
                                          >
                                            {product.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`products[${index}].staff_id`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Stylist
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      value={
                                        field.state.value
                                          ? String(field.state.value)
                                          : undefined
                                      }
                                      onValueChange={(value) =>
                                        field.handleChange(Number(value))
                                      }
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {staffList.map((member) => (
                                          <SelectItem
                                            key={member.id}
                                            value={String(member.id)}
                                          >
                                            {staffLabel(member)}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`products[${index}].price`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Price
                                      <RequiredInput />
                                    </FieldLabel>
                                    <div className="relative">
                                      <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                                      >
                                        $
                                      </span>
                                      <Input
                                        id={field.name}
                                        name={field.name}
                                        type="number"
                                        step="0.01"
                                        value={field.state.value}
                                        onBlur={field.handleBlur}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        aria-invalid={isInvalid}
                                        className="pl-6"
                                      />
                                    </div>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="mt-7"
                              aria-label="Remove product"
                              onClick={() => linesField.removeValue(index)}
                            >
                              <Trash2 />
                            </Button>
                          </div>
                        );
                      }}
                    />
                  ))}
                </>
              )}
            />

            <Separator />

            <div className="flex items-end justify-between gap-4">
              <form.Field
                name="discount_percent"
                children={(field) => {
                  const isInvalid =
                    field.state.meta.isTouched && !field.state.meta.isValid;
                  return (
                    <Field data-invalid={isInvalid} className="max-w-32">
                      <FieldLabel htmlFor={field.name}>Discount</FieldLabel>
                      <div className="relative">
                        <Input
                          id={field.name}
                          name={field.name}
                          type="number"
                          step="0.01"
                          min={0}
                          max={100}
                          value={field.state.value}
                          onBlur={field.handleBlur}
                          onChange={(e) => field.handleChange(e.target.value)}
                          aria-invalid={isInvalid}
                          className="pr-8"
                        />
                        <span
                          aria-hidden="true"
                          className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                        >
                          %
                        </span>
                      </div>
                      {isInvalid && (
                        <FieldError errors={field.state.meta.errors} />
                      )}
                    </Field>
                  );
                }}
              />
              <form.Subscribe
                selector={(state) => ({
                  services: state.values.services,
                  products: state.values.products,
                  discount_percent: state.values.discount_percent,
                })}
                children={({ services, products, discount_percent }) => (
                  <p className="text-xl font-bold">
                    Total{" "}
                    {intl.formatNumber(computeSaleFormTotal(
                        services.map((line) => line.price),
                        products.map((line) => line.price),
                        discount_percent
                      ), { format: "money" })}
                  </p>
                )}
              />
            </div>

            <Separator />

            <form.Field
              name="payments"
              mode="array"
              children={(linesField) => (
                <>
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-semibold">Payment</p>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label="Add payment"
                      onClick={() =>
                        linesField.pushValue({ ...EMPTY_SALE_PAYMENT_LINE })
                      }
                    >
                      <Plus />
                    </Button>
                  </div>
                  {linesField.state.value.map((_, index) => (
                    <form.Subscribe
                      key={index}
                      selector={(state) => state.values.payments[index]}
                      children={(line) => {
                        if (!line) return null;
                        return (
                          <div className={SALE_PAYMENT_ROW_GRID}>
                            <form.Field
                              name={`payments[${index}].method`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Method
                                      <RequiredInput />
                                    </FieldLabel>
                                    <Select
                                      value={field.state.value || undefined}
                                      onValueChange={(value) =>
                                        field.handleChange(
                                          value as typeof field.state.value
                                        )
                                      }
                                    >
                                      <SelectTrigger
                                        id={field.name}
                                        aria-invalid={isInvalid}
                                      >
                                        <SelectValue placeholder="Select..." />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {PAYMENT_METHOD_OPTIONS.map((option) => (
                                          <SelectItem
                                            key={option.value}
                                            value={option.value}
                                          >
                                            {option.label}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            <form.Field
                              name={`payments[${index}].amount`}
                              children={(field) => {
                                const isInvalid =
                                  field.state.meta.isTouched &&
                                  !field.state.meta.isValid;
                                return (
                                  <Field data-invalid={isInvalid}>
                                    <FieldLabel htmlFor={field.name}>
                                      Amount
                                      <RequiredInput />
                                    </FieldLabel>
                                    <div className="relative">
                                      <span
                                        aria-hidden="true"
                                        className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-base md:text-sm"
                                      >
                                        $
                                      </span>
                                      <Input
                                        id={field.name}
                                        name={field.name}
                                        type="number"
                                        step="0.01"
                                        value={field.state.value}
                                        onBlur={field.handleBlur}
                                        onChange={(e) => field.handleChange(e.target.value)}
                                        aria-invalid={isInvalid}
                                        className="pl-6"
                                      />
                                    </div>
                                    {isInvalid && (
                                      <FieldError
                                        errors={field.state.meta.errors}
                                      />
                                    )}
                                  </Field>
                                );
                              }}
                            />
                            {linesField.state.value.length > 1 ? (
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="mt-7"
                                aria-label="Remove payment"
                                onClick={() => linesField.removeValue(index)}
                              >
                                <Trash2 />
                              </Button>
                            ) : (
                              <div />
                            )}
                          </div>
                        );
                      }}
                    />
                  ))}
                  <form.Subscribe
                    selector={(state) =>
                      paymentsMatchTotal(
                        state.values.payments.map((line) => line.amount),
                        computeSaleFormTotal(
                          state.values.services.map((line) => line.price),
                          state.values.products.map((line) => line.price),
                          state.values.discount_percent
                        )
                      )
                    }
                    children={(matches) =>
                      matches ? null : (
                        <Field data-invalid>
                          <FieldError>
                            Payments must equal the total
                          </FieldError>
                        </Field>
                      )
                    }
                  />
                </>
              )}
            />
          </FieldGroup>
        </form>

        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline" className="w-24">
              Cancel
            </Button>
          </DialogClose>
          <form.Subscribe
            selector={(state) =>
              paymentsMatchTotal(
                state.values.payments.map((line) => line.amount),
                computeSaleFormTotal(
                  state.values.services.map((line) => line.price),
                  state.values.products.map((line) => line.price),
                  state.values.discount_percent
                )
              )
            }
            children={(matches) => (
              <Button
                type="submit"
                form="walk-in-sale-form"
                className="w-24"
                disabled={!matches || createWalkIn.isPending}
              >
                Complete
              </Button>
            )}
          />
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
